import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../config/api';
import { calculateServiceCharge } from '../../utils/calculations';
import OrderBillSender from '../../components/OrderBillSender';
import AlertMessage from '../../components/AlertMessage';

// CSS for loading animation data
const loadingStyles = `
  @keyframes loading {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
    20%, 40%, 60%, 80% { transform: translateX(2px); }
  }
  .animate-shake {
    animation: shake 0.5s ease-in-out;
  }
`;

const CounterOrder = ({ isFullscreen = false }) => {
  const navigate = useNavigate();
  const { restaurantId } = useParams();

  // Local state management (no Redux)
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalOrders, setTotalOrders] = useState(0);

  // Modal and form states
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderForm, setOrderForm] = useState({
    customerName: '',
    mobileNumber: '',
    message: '',
    items: []
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  
  // Tab states
  const [activeTab, setActiveTab] = useState('counter');
  const [tableOrders, setTableOrders] = useState([]);
  const [tableOrdersLoading, setTableOrdersLoading] = useState(false);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());
  const [showStats, setShowStats] = useState(false);

  const restaurantData = localStorage.getItem('restaurantData')
    ? JSON.parse(localStorage.getItem('restaurantData'))
    : null;
  const actualRestaurantId = restaurantId || restaurantData?._id || null;
  // Direct API calls without Redux
  const fetchCounterOrders = useCallback(async (page = 1, limit = 20, showRefreshLoading = false) => {
    if (!actualRestaurantId) return;

    if (showRefreshLoading) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      console.log(`Fetching orders for restaurant: ${actualRestaurantId}`);
      
      const response = await axios.get(`${API_BASE_URL}/orders/counter/${actualRestaurantId}`, {
        params: { page, limit },
        timeout: 10000
      });

      console.log('Orders fetched successfully:', response.data);
      
      setOrders(response.data.orders || []);
      setTotalOrders(response.data.totalOrders || 0);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch orders');
      setOrders([]);
    } finally {
      if (showRefreshLoading) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [actualRestaurantId]);

  // Handle refresh button click
  const handleRefresh = () => {
    if (activeTab === 'counter') {
      fetchCounterOrders(1, 20, true);
    } else {
      fetchTableOrders(true);
    }
  };

  const fetchMenuItems = useCallback(async () => {
    if (!actualRestaurantId) return;

    try {
      console.log(`Fetching menu items for restaurant: ${actualRestaurantId}`);
      
      const response = await axios.get(`${API_BASE_URL}/menu?restaurantId=${actualRestaurantId}`, {
        timeout: 10000
      });

      console.log('Menu items fetched successfully:', response.data);
      setMenuItems(response.data || []);
    } catch (err) {
      console.error('Error fetching menu items:', err);
      setMenuItems([]);
    }
  }, [actualRestaurantId]);

  // Fetch table orders
  const fetchTableOrders = useCallback(async (showRefreshLoading = false) => {
    if (!actualRestaurantId) return;

    if (showRefreshLoading) {
      setIsRefreshing(true);
    } else {
      setTableOrdersLoading(true);
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/orders`, {
        params: { 
          restaurantId: actualRestaurantId,
          orderType: 'table',
          limit: 50
        },
        timeout: 10000
      });
      setTableOrders(response.data || []);
    } catch (err) {
      console.error('Error fetching table orders:', err);
      setTableOrders([]);
    } finally {
      if (showRefreshLoading) {
        setIsRefreshing(false);
      } else {
        setTableOrdersLoading(false);
      }
    }
  }, [actualRestaurantId]);

  // Initialize data on component mount
  useEffect(() => {
    if (actualRestaurantId) {
      console.log('Initializing CounterOrder (No Redux) for restaurant:', actualRestaurantId);
      fetchCounterOrders();
      fetchMenuItems();
      fetchTableOrders();
    }
  }, [actualRestaurantId, fetchCounterOrders, fetchMenuItems, fetchTableOrders]);

  // Helper functions
  const calculateTotal = useCallback(() => {
    const subtotal = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const taxRate = restaurantData?.taxPercentage || 0; // 0% GST
    const serviceRate = calculateServiceCharge(subtotal);

    const taxAmount = (subtotal * taxRate)*0.01.toFixed(2);
    const serviceAmount = serviceRate.toFixed(2);
    const finalAmount = (Number(subtotal) + (Number(taxAmount) + Number(serviceAmount))).toFixed(2);

    return {
      baseAmount: subtotal,
      taxAmount,
      serviceAmount,
      finalAmount
    };
  }, [selectedItems, restaurantData]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  }, []);

  const formatDate = useCallback((date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Event handlers
  const handleAddItem = useCallback((item) => {
    const existingItem = selectedItems.find(selected => selected.itemId === item._id);
    if (existingItem) {
      setSelectedItems(selectedItems.map(selected =>
        selected.itemId === item._id
          ? { ...selected, quantity: selected.quantity + 1 }
          : selected
      ));
    } else {
      setSelectedItems([...selectedItems, {
        itemId: item._id,
        name: item.itemName,
        price: item.itemPrice,
        quantity: 1
      }]);
    }
  }, [selectedItems]);

  const handleRemoveItem = useCallback((itemId) => {
    setSelectedItems(selectedItems.filter(item => item.itemId !== itemId));
  }, [selectedItems]);

  const handleUpdateQuantity = useCallback((itemId, quantity) => {
    if (quantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }
    setSelectedItems(selectedItems.map(item =>
      item.itemId === itemId ? { ...item, quantity } : item
    ));
  }, [selectedItems, handleRemoveItem]);

  const handleCreateOrder = async () => {
    // Clear previous modal errors
    setModalError('');
    
    // Validate required fields with specific error messages
    if (!orderForm.customerName.trim()) {
      setModalError('⚠️ Customer name is required');
      return;
    }
    
    // Mobile number is optional, but if provided, it must be exactly 10 digits
    if (orderForm.mobileNumber.trim() && orderForm.mobileNumber.length !== 10) {
      setModalError('⚠️ Mobile number must be exactly 10 digits');
      return;
    }
    
    if (selectedItems.length === 0) {
      setModalError('⚠️ Please add at least one item to the order');
      return;
    }

    setLoading(true);
    setIsCreatingOrder(true);
    setModalError('');
    setModalSuccess('');
    try {
      const totals = calculateTotal();
      const orderData = {
        restaurantId: actualRestaurantId,
        customerName: orderForm.customerName,
        mobileNumber: orderForm.mobileNumber,
        message: orderForm.message,
        items: selectedItems,
        baseAmount: totals.baseAmount,
        taxAmount: totals.taxAmount,
        serviceAmount: totals.serviceAmount,
        finalAmount: totals.finalAmount,
        orderType: 'counter'
      };

      if (editingOrder) {
        // Update existing order
        const response = await axios.put(`${API_BASE_URL}/orders/${editingOrder._id}`, orderData, { timeout: 10000 });
        const updatedOrder = { ...response.data, orderDate: response.data.orderDate || new Date().toISOString() };
        // Update the order in local state
        setOrders(prevOrders => prevOrders.map(order => 
          order._id === editingOrder._id ? updatedOrder : order
        ));
        setModalSuccess('✅ Order updated successfully!');
      } else {
        // Create new order
        const response = await axios.post(`${API_BASE_URL}/orders/counter/add`, orderData, { timeout: 10000 });
        // Add new order to local state immediately
        const newOrder = { ...response.data, orderDate: response.data.orderDate || new Date().toISOString() };
        setOrders(prevOrders => [newOrder, ...prevOrders]);
        setTotalOrders(prev => prev + 1);
        setModalSuccess('✅ Order created successfully!');
      }
      
      // Reset form after a short delay to show success message
      setTimeout(() => {
        setOrderForm({ customerName: '', mobileNumber: '', message: '', items: [] });
        setSelectedItems([]);
        setModalError('');
        setModalSuccess('');
        setShowOrderForm(false);
        setEditingOrder(null);
      }, 1500);
    } catch (err) {
      setModalError(err.response?.data?.message || `Failed to ${editingOrder ? 'update' : 'create'} order`);
      setError(err.response?.data?.message || `Failed to ${editingOrder ? 'update' : 'create'} order`);
    } finally {
      setLoading(false);
      setIsCreatingOrder(false);
    }
  };

  const handleDeleteOrder = (orderId) => {
    setOrderToDelete(orderId);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/orders/${orderToDelete}`, { timeout: 10000 });
      await fetchCounterOrders(); // Refresh orders
      setShowDeleteModal(false);
      setOrderToDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete order');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order) => {
    console.log('Viewing order:', order);
    setViewingOrder(order);
    setShowOrderDetails(true);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setOrderForm({
      customerName: order.customerName || '',
      mobileNumber: order.mobileNumber || '',
      message: order.message || '',
      items: order.items || []
    });
    setSelectedItems(order.items?.map(item => ({
      itemId: item.itemId || item._id,
      name: item.name,
      price: item.price,
      quantity: item.quantity
    })) || []);
    setShowOrderForm(true);
  };

  const toggleOrderCollapsed = (orderId) => {
    setExpandedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  // Filter orders based on search and date
  const getFilteredOrders = (ordersList = orders) => {
    if (!Array.isArray(ordersList)) return [];
    
    let filtered = [...ordersList];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.mobileNumber?.includes(searchTerm) ||
        order._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.tableId?.tableNumber?.toString().includes(searchTerm)
      );
    }
    
    // Apply date filter
    if (dateFilter !== 'all') {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderDate);
        const now = new Date();
        
        switch (dateFilter) {
          case 'today': {
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const end = new Date(start);
            end.setDate(end.getDate() + 1);
            return orderDate >= start && orderDate < end;
          }
          case 'yesterday': {
            const yStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
            const yEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            return orderDate >= yStart && orderDate < yEnd;
          }
          case 'week': {
            const weekAgo = new Date(now);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return orderDate >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(now);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return orderDate >= monthAgo;
          }
          default:
            return true;
        }
      });
    }

    return filtered.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
  };

  const filteredOrders = getFilteredOrders();
  const filteredTableOrders = getFilteredOrders(tableOrders);
  const totals = calculateTotal();

  // Statistics calculations
  const todayOrders = orders.filter(order => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(order.orderDate) >= today;
  });

  const todayRevenue = todayOrders.reduce((sum, order) => sum + (order.finalAmount || 0), 0);
  const avgOrderValue = todayOrders.length > 0 ? todayRevenue / todayOrders.length : 0;

  if (!actualRestaurantId) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <p className="text-gray-600">No restaurant selected</p>
        </div>
      </div>
    );
  }

  const currentOrderCount = activeTab === 'counter' ? filteredOrders.length : filteredTableOrders.length;

  return (
    <div className="relative min-h-[60vh] overflow-hidden">
      <style>{loadingStyles}</style>
      {loading && !isRefreshing && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-1 bg-orange-100 overflow-hidden">
            <div className="h-1 bg-orange-500 w-full" style={{ animation: 'loading 1.5s ease-in-out infinite' }} />
          </div>
        </div>
      )}

      {/* Compact sticky header — title + actions in one tight row */}
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200 shadow-sm">
        <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-4 sm:py-2">
          <div className="flex items-center min-w-0">

              <h2 className="text-base sm:text-xl font-bold text-stone-800 truncate">{activeTab === 'counter' ? 'Counter Orders' : 'Table Orders'}</h2>

          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading || tableOrdersLoading || isRefreshing}
              className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
              title="Refresh"
              aria-label="Refresh"
            >
              {isRefreshing ? (
                <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="text-sm">↻</span>
              )}
            </button>
            {!isFullscreen && (
              <button
                type="button"
                onClick={() => navigate('/restaurant/counter-fullscreen')}
                className="inline-flex items-center justify-center p-2 rounded-lg font-semibold text-sm bg-stone-100 text-stone-700 hover:bg-stone-200 size-9"
                title="Fullscreen"
                aria-label="Fullscreen"
              >
                ⤢
              </button>
            )}
            {activeTab === 'counter' && (
              <button
                type="button"
                onClick={() => setShowOrderForm(true)}
                disabled={loading}
                className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg font-semibold text-xs sm:text-sm bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 shadow-sm h-9"
              >
                New Order
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-5">
        {/* Tabs + summary toggle in one row */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('counter')}
              className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                activeTab === 'counter'
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
              }`}
            >
              Counter Orders
            </button>
            <button
              onClick={() => setActiveTab('table')}
              className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                activeTab === 'table'
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
              }`}
            >
              Table Orders
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowStats(prev => !prev)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200 border border-stone-200 transition-colors"
          >
            {showStats ? 'Hide summary' : 'Show summary'}
          </button>
        </div>
        {showStats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
              <p className="text-stone-500 text-xs sm:text-sm font-medium">Today&apos;s Orders</p>
              <p className="text-lg sm:text-2xl font-bold text-stone-800 mt-0.5 break-words leading-tight">
                {activeTab === 'counter' ? todayOrders.length : tableOrders.filter(o => new Date(o.orderDate) >= new Date(new Date().setHours(0, 0, 0, 0))).length}
              </p>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
              <p className="text-stone-500 text-xs sm:text-sm font-medium">Today&apos;s Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600 mt-0.5 break-words leading-tight">
                {activeTab === 'counter' ? formatCurrency(todayRevenue) : formatCurrency(tableOrders.filter(o => new Date(o.orderDate) >= new Date(new Date().setHours(0, 0, 0, 0))).reduce((s, o) => s + (o.finalAmount || 0), 0))}
              </p>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
              <p className="text-stone-500 text-xs sm:text-sm font-medium">Avg Order</p>
              <p className="text-lg sm:text-2xl font-bold text-emerald-600 mt-0.5 break-words leading-tight">
                {activeTab === 'counter' ? formatCurrency(avgOrderValue) : (() => {
                  const todayTable = tableOrders.filter(o => new Date(o.orderDate) >= new Date(new Date().setHours(0, 0, 0, 0)));
                  const rev = todayTable.reduce((s, o) => s + (o.finalAmount || 0), 0);
                  return formatCurrency(todayTable.length ? rev / todayTable.length : 0);
                })()}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4">
            <AlertMessage message={error} type="error" onDismiss={() => setError(null)} />
          </div>
        )}

        {/* Filters — search + date, same row as Order Flow */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="relative flex-1 max-w-md">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">🔍</span>
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-stone-800 placeholder:text-stone-400"
              disabled={loading}
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <label htmlFor="counter-date-filter" className="text-xs sm:text-sm font-medium text-stone-600 whitespace-nowrap">Date</label>
            <select
              id="counter-date-filter"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border border-stone-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 min-w-[120px] bg-white text-stone-800"
              disabled={loading}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
            </select>
          </div>
        </div>

        {(loading || tableOrdersLoading) && currentOrderCount === 0 && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-400 border-t-transparent" />
          </div>
        )}

        {!loading && !tableOrdersLoading && ((activeTab === 'counter' && filteredOrders.length === 0) || (activeTab === 'table' && filteredTableOrders.length === 0)) && (
          <div className="text-center py-12 text-stone-500">
            <p className="text-4xl mb-2">🛒</p>
            <p className="text-sm font-medium">
              {activeTab === 'counter'
                ? (orders.length === 0 ? 'No counter orders yet. Create your first order!' : 'No orders match your search.')
                : (tableOrders.length === 0 ? 'No table orders.' : 'No table orders match your search.')}
            </p>
          </div>
        )}

        {!loading && !tableOrdersLoading && ((activeTab === 'counter' && filteredOrders.length > 0) || (activeTab === 'table' && filteredTableOrders.length > 0)) && (
          <div className="space-y-2">
            {(activeTab === 'counter' ? filteredOrders : filteredTableOrders).map((order) => {
              const isCollapsed = !expandedOrderIds.has(order._id);
              return (
                <div
                  key={order._id}
                  className="bg-white rounded-lg shadow-sm border border-stone-200 border-t-2 border-t-orange-400 overflow-hidden"
                >
                  {/* Collapsed: single compact row */}
                  <div className={`flex items-center justify-between gap-2 ${isCollapsed ? 'py-2 px-3 sm:px-4' : 'pt-2 px-3 pb-3 sm:pt-2.5 sm:px-4 sm:pb-4'}`}>
                    <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                      <h3 className="text-sm sm:text-base font-bold text-stone-800 truncate">{order.customerName}</h3>
                      <span className="text-xs text-stone-500 flex-shrink-0">#{order._id?.slice(-6)}</span>
                      {activeTab === 'table' && order.tableId && (
                        <span className="px-2 py-0.5 bg-orange-50 text-orange-700 text-xs font-medium rounded-md border border-orange-200 flex-shrink-0">
                          Table {order.tableId.tableNumber || order.tableId}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm sm:text-base font-bold text-orange-600 tabular-nums">
                        {formatCurrency(order.finalAmount)}
                      </span>
                      <button
                        onClick={() => toggleOrderCollapsed(order._id)}
                        className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200 border border-stone-200 transition-colors"
                        disabled={loading || tableOrdersLoading}
                      >
                        {isCollapsed ? 'Show' : 'Hide'}
                      </button>
                    </div>
                  </div>
                  {/* Expanded details */}
                  {!isCollapsed && (
                    <div className="border-t border-stone-100 px-3 pt-2 pb-3 sm:px-4 sm:pt-2.5 sm:pb-4 space-y-1.5">
                      <p className="text-stone-600 text-xs sm:text-sm">📱 {order.mobileNumber || '—'}</p>
                      <p className="text-stone-500 text-xs sm:text-sm">📅 {formatDate(order.orderDate)}</p>
                      {order.items && order.items.length > 0 && (
                        <p className="text-stone-600 text-xs sm:text-sm">
                          <span className="font-medium text-stone-700">Items:</span>{' '}
                          {order.items.map(item => `${item.name} (×${item.quantity})`).join(', ')}
                        </p>
                      )}
                      <div className="pt-1 flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200 border border-stone-200 transition-colors"
                          disabled={loading || tableOrdersLoading}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEditOrder(order)}
                          className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200 transition-colors"
                          disabled={loading || tableOrdersLoading}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order._id)}
                          className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 transition-colors"
                          disabled={loading || tableOrdersLoading}
                        >
                          Delete
                        </button>
                      </div>
                      <div className="pt-1">
                        <OrderBillSender orderId={order._id} order={order} showFeedback={false} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Details Modal — redesigned */}
      <AnimatePresence>
        {showOrderDetails && viewingOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
            onClick={() => setShowOrderDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col border border-stone-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-stone-200 bg-stone-50/80 flex-shrink-0">
                <h2 className="text-base font-bold text-stone-800">Order Details</h2>
                <button
                  type="button"
                  onClick={() => setShowOrderDetails(false)}
                  className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-200 hover:text-stone-800 transition-colors text-lg leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 p-3 sm:p-4">
                {/* Customer & order info */}
                <div className="mb-4 p-3 rounded-xl bg-stone-50 border border-stone-200">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-2">Customer & order</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <span className="text-stone-500 block">Name</span>
                      <span className="font-medium text-stone-800">{viewingOrder.customerName}</span>
                    </div>
                    <div>
                      <span className="text-stone-500 block">Mobile</span>
                      <span className="font-medium text-stone-800">{viewingOrder.mobileNumber || '—'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-stone-500 block">Order ID</span>
                      <span className="font-mono text-stone-800 text-[11px] break-all">#{viewingOrder._id?.slice(-8)}</span>
                    </div>
                    <div>
                      <span className="text-stone-500 block">Date</span>
                      <span className="font-medium text-stone-800">{formatDate(viewingOrder.orderDate)}</span>
                    </div>
                    {viewingOrder.tableId && (
                      <div>
                        <span className="text-stone-500 block">Table</span>
                        <span className="font-medium text-stone-800">Table {viewingOrder.tableId.tableNumber ?? viewingOrder.tableId}</span>
                      </div>
                    )}
                    {viewingOrder.message && (
                      <div className="col-span-2">
                        <span className="text-stone-500 block">Note</span>
                        <span className="text-stone-800 text-xs">{viewingOrder.message}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order items */}
                <div className="mb-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Items</p>
                  <div className="space-y-1.5">
                    {viewingOrder.items?.map((item, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center gap-2 p-2.5 rounded-lg bg-orange-50/80 border border-orange-100"
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-xs text-stone-800 block break-words">{item.name}</span>
                          <span className="text-stone-500 text-[10px]">{item.quantity} × {formatCurrency(item.price)}</span>
                        </div>
                        <span className="text-xs font-semibold text-orange-600 tabular-nums flex-shrink-0">
                          {formatCurrency(item.price * item.quantity)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="mb-4 p-2.5 rounded-lg bg-stone-100 border border-stone-200">
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between text-stone-600">
                      <span>Subtotal</span>
                      <span className="tabular-nums">{formatCurrency(Number(viewingOrder.finalAmount) - Number(viewingOrder.taxAmount) - Number(viewingOrder.serviceAmount))}</span>
                    </div>
                    <div className="flex justify-between text-stone-600">
                      <span>Tax</span>
                      <span className="tabular-nums">{formatCurrency(viewingOrder.taxAmount)}</span>
                    </div>
                    <div className="flex justify-between text-stone-600">
                      <span>Service</span>
                      <span className="tabular-nums">{formatCurrency(viewingOrder.serviceAmount)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-sm text-stone-800 border-t border-stone-300 pt-1.5 mt-1.5">
                      <span>Total</span>
                      <span className="text-orange-600 tabular-nums">{formatCurrency(viewingOrder.finalAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Send bill */}
                <div>
                  <OrderBillSender orderId={viewingOrder._id} order={viewingOrder} showFeedback={false} />
                </div>
              </div>

              {/* Footer actions */}
              <div className="flex gap-2 px-3 sm:px-4 py-2.5 border-t border-stone-200 bg-white flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderDetails(false);
                    handleEditOrder(viewingOrder);
                  }}
                  className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                >
                  Edit Order
                </button>
                <button
                  type="button"
                  onClick={() => setShowOrderDetails(false)}
                  className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create/Edit Order Modal — revamped */}
      <AnimatePresence>
        {showOrderForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50"
            onClick={() => {
              setShowOrderForm(false);
              setEditingOrder(null);
              setOrderForm({ customerName: '', mobileNumber: '', message: '', items: [] });
              setSelectedItems([]);
              setModalError('');
              setModalSuccess('');
              setIsCreatingOrder(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col border border-stone-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-stone-200 bg-stone-50/80 flex-shrink-0">
                <h2 className="text-base font-bold text-stone-800">
                  {editingOrder ? 'Edit Order' : 'Create New Order'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderForm(false);
                    setEditingOrder(null);
                    setOrderForm({ customerName: '', mobileNumber: '', message: '', items: [] });
                    setSelectedItems([]);
                    setModalError('');
                    setModalSuccess('');
                    setIsCreatingOrder(false);
                  }}
                  className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-200 hover:text-stone-800 transition-colors text-lg leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 p-3 sm:p-4">
                {modalError && (
                  <div className="mb-3">
                    <AlertMessage
                      message={modalError}
                      type="error"
                      onDismiss={() => setModalError('')}
                      animate={true}
                    />
                  </div>
                )}
                {modalSuccess && (
                  <div className="mb-3">
                    <AlertMessage
                      message={modalSuccess}
                      type="success"
                      onDismiss={() => setModalSuccess('')}
                      animate={false}
                    />
                  </div>
                )}
                {isCreatingOrder && (
                  <div className="mb-3 flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-400 border-t-transparent flex-shrink-0" />
                    <p className="text-orange-800 font-medium text-xs">
                      {editingOrder ? 'Updating order...' : 'Creating order...'}
                    </p>
                  </div>
                )}

                {/* Customer details */}
                <div className="mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Customer</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Customer name *"
                      value={orderForm.customerName}
                      onChange={(e) => setOrderForm({ ...orderForm, customerName: e.target.value })}
                      className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-stone-800 placeholder:text-stone-400"
                    />
                    <input
                      type="tel"
                      placeholder="Mobile (optional)"
                      maxLength={10}
                      value={orderForm.mobileNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setOrderForm({ ...orderForm, mobileNumber: value });
                      }}
                      className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-stone-800 placeholder:text-stone-400"
                    />
                  </div>
                </div>

                {/* Add items */}
                <div className="mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Add items</p>
                  <input
                    type="text"
                    placeholder="Search menu..."
                    value={menuSearchTerm}
                    onChange={(e) => setMenuSearchTerm(e.target.value)}
                    className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none mb-1.5 text-stone-800 placeholder:text-stone-400"
                  />
                  <div className="max-h-28 sm:max-h-32 overflow-y-auto rounded-lg border border-stone-200 divide-y divide-stone-100">
                    {menuItems
                      .filter(item => item.itemName?.toLowerCase().includes(menuSearchTerm.toLowerCase()))
                      .map(item => (
                        <div
                          key={item._id}
                          className="flex items-center justify-between gap-2 px-2.5 py-2 hover:bg-stone-50 transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <span className="font-medium text-xs text-stone-800 block truncate">{item.itemName}</span>
                            <span className="text-stone-500 text-[10px]">{formatCurrency(item.itemPrice)}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddItem(item)}
                            className="flex-shrink-0 px-2 py-1 text-[10px] font-semibold rounded bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Selected items */}
                {selectedItems.length > 0 && (
                  <>
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                        Selected ({selectedItems.length})
                      </p>
                      <div className="space-y-1.5">
                        {selectedItems.map(item => (
                          <div
                            key={item.itemId}
                            className="flex items-center justify-between gap-2 p-2 rounded-lg bg-orange-50/80 border border-orange-100"
                          >
                            <span className="font-medium text-xs text-stone-800 flex-1 min-w-0 break-words">{item.name}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <div className="inline-flex items-center gap-0.5 rounded bg-white border border-orange-200 p-0.5">
                                <button
                                  type="button"
                                  onClick={() => handleUpdateQuantity(item.itemId, item.quantity - 1)}
                                  className="w-6 h-6 flex items-center justify-center rounded text-stone-600 hover:bg-orange-100 font-semibold text-xs"
                                >
                                  −
                                </button>
                                <span className="w-5 text-center text-xs font-semibold text-stone-800">{item.quantity}</span>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateQuantity(item.itemId, item.quantity + 1)}
                                  className="w-6 h-6 flex items-center justify-center rounded text-stone-600 hover:bg-orange-100 font-semibold text-xs"
                                >
                                  +
                                </button>
                              </div>
                              <span className="text-xs font-semibold text-orange-600 w-14 text-right tabular-nums">
                                {formatCurrency(item.price * item.quantity)}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(item.itemId)}
                                className="p-1 rounded text-stone-400 hover:bg-red-50 hover:text-red-600 transition-colors text-sm leading-none"
                                aria-label="Remove item"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order total */}
                    <div className="mb-2 p-2.5 rounded-lg bg-stone-100 border border-stone-200">
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between text-stone-600">
                          <span>Subtotal</span>
                          <span className="tabular-nums">{formatCurrency(totals.baseAmount)}</span>
                        </div>
                        <div className="flex justify-between text-stone-600">
                          <span>Tax ({restaurantData?.taxPercentage || 0}%)</span>
                          <span className="tabular-nums">{formatCurrency(totals.taxAmount)}</span>
                        </div>
                        <div className="flex justify-between text-stone-600">
                          <span>Service</span>
                          <span className="tabular-nums">{formatCurrency(totals.serviceAmount)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-sm text-stone-800 border-t border-stone-300 pt-1.5 mt-1.5">
                          <span>Total</span>
                          <span className="text-orange-600 tabular-nums">{formatCurrency(totals.finalAmount)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Sticky footer actions */}
              <div className="flex gap-2 px-3 sm:px-4 py-2.5 border-t border-stone-200 bg-white flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderForm(false);
                    setEditingOrder(null);
                    setOrderForm({ customerName: '', mobileNumber: '', message: '', items: [] });
                    setSelectedItems([]);
                    setModalError('');
                    setModalSuccess('');
                    setIsCreatingOrder(false);
                  }}
                  className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateOrder}
                  disabled={isCreatingOrder || !orderForm.customerName || selectedItems.length === 0}
                  className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:pointer-events-none transition-colors flex items-center justify-center gap-1.5"
                >
                  {isCreatingOrder && (
                    <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {isCreatingOrder
                    ? (editingOrder ? 'Updating...' : 'Creating...')
                    : (editingOrder ? 'Update Order' : 'Create Order')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this order? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setOrderToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteOrder}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CounterOrder;
